import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from '../utils/safeToast';
import { Plus, Search, Edit, Trash2, Loader2, RotateCcw, Building2, Key, FileText, Printer, Settings } from 'lucide-react';

const API = "/api";

const CleaningCompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    contact_salutation: '',
    email: '',
    phone: '',
    notes: '',
    cleaning_protocol_notes: ''
  });
  
  // Linked properties & key handover
  const [linkedProperties, setLinkedProperties] = useState([]);
  const [keyHandoverData, setKeyHandoverData] = useState({});
  const [loadingLinked, setLoadingLinked] = useState(false);
  
  // Central cleaning protocol text
  const [centralProtocolText, setCentralProtocolText] = useState('');
  const [savingProtocol, setSavingProtocol] = useState(false);
  
  // Settings dialog for central text
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [centralTextEdit, setCentralTextEdit] = useState('');
  
  const printRef = useRef(null);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cleaning-companies`, {
        params: { include_inactive: showInactive }
      });
      setCompanies(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Reinigungsfirmen');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchCompanies();
    fetchCentralProtocolText();
  }, [fetchCompanies]);

  const fetchCentralProtocolText = async () => {
    try {
      const res = await axios.get(`${API}/settings/cleaning-protocol-text`);
      setCentralProtocolText(res.data.text || '');
    } catch (error) {
      console.error('Error fetching central protocol text:', error);
    }
  };

  const fetchLinkedProperties = async (companyId) => {
    setLoadingLinked(true);
    try {
      const res = await axios.get(`${API}/cleaning-companies/${companyId}/linked-properties`);
      setLinkedProperties(res.data.properties || []);
      // Initialize key handover data
      const keyData = {};
      (res.data.properties || []).forEach(p => {
        keyData[p.id] = p.key_count || 1;
      });
      setKeyHandoverData(keyData);
    } catch (error) {
      toast.error('Fehler beim Laden der verknüpften Objekte');
      setLinkedProperties([]);
    } finally {
      setLoadingLinked(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      contact_salutation: '',
      email: '',
      phone: '',
      notes: '',
      cleaning_protocol_notes: ''
    });
    setLinkedProperties([]);
    setKeyHandoverData({});
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name || '',
      contact_person: company.contact_person || '',
      contact_salutation: company.contact_salutation || '',
      email: company.email || '',
      phone: company.phone || '',
      notes: company.notes || '',
      cleaning_protocol_notes: company.cleaning_protocol_notes || ''
    });
    fetchLinkedProperties(company.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Name und E-Mail sind erforderlich');
      return;
    }
    setSubmitting(true);
    try {
      if (selectedCompany) {
        await axios.put(`${API}/cleaning-companies/${selectedCompany.id}`, formData);
        toast.success('Reinigungsfirma aktualisiert');
      } else {
        await axios.post(`${API}/cleaning-companies`, formData);
        toast.success('Reinigungsfirma erstellt');
      }
      setDialogOpen(false);
      resetForm();
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCompany) return;
    try {
      await axios.delete(`${API}/cleaning-companies/${selectedCompany.id}`);
      toast.success('Reinigungsfirma deaktiviert');
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Deaktivieren');
    }
  };

  const handleReactivate = async (company) => {
    try {
      await axios.put(`${API}/cleaning-companies/${company.id}`, { status: 'aktiv' });
      toast.success('Reinigungsfirma reaktiviert');
      fetchCompanies();
    } catch (error) {
      toast.error('Fehler beim Reaktivieren');
    }
  };

  const handleKeyCountChange = (propertyId, value) => {
    setKeyHandoverData(prev => ({
      ...prev,
      [propertyId]: parseInt(value) || 1
    }));
  };

  const saveKeyHandover = async () => {
    if (!selectedCompany) return;
    setSavingProtocol(true);
    try {
      const entries = Object.entries(keyHandoverData).map(([property_id, key_count]) => ({
        property_id,
        key_count
      }));
      await axios.put(`${API}/cleaning-companies/${selectedCompany.id}/key-handover`, { entries });
      toast.success('Schlüsselprotokoll gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern des Schlüsselprotokolls');
    } finally {
      setSavingProtocol(false);
    }
  };

  const saveCentralProtocolText = async () => {
    setSavingProtocol(true);
    try {
      await axios.put(`${API}/settings/cleaning-protocol-text`, { text: centralTextEdit });
      setCentralProtocolText(centralTextEdit);
      toast.success('Zentraler Standardtext gespeichert');
      setSettingsDialogOpen(false);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingProtocol(false);
    }
  };

  const getTotalKeys = () => {
    return Object.values(keyHandoverData).reduce((sum, count) => sum + (parseInt(count) || 0), 0);
  };

  const printKeyProtocol = () => {
    const printWindow = window.open('', '_blank');
    const today = new Date().toLocaleDateString('de-DE');
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Schlüsselübergabeprotokoll - ${selectedCompany?.name}</title>
        <style>
          @page { size: A4; margin: 20mm; }
          body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.4; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #0E4C92; padding-bottom: 15px; }
          .header h1 { color: #0E4C92; margin: 0 0 10px 0; font-size: 18pt; }
          .company-info { background: #f5f5f5; padding: 15px; margin-bottom: 25px; border-left: 4px solid #0E4C92; }
          .company-info h3 { margin: 0 0 10px 0; color: #0E4C92; }
          .meta { margin-bottom: 20px; }
          .meta span { margin-right: 30px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #0E4C92; color: white; padding: 10px; text-align: left; }
          td { padding: 10px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background: #f9f9f9; }
          .total-row { font-weight: bold; background: #e8f4fc !important; }
          .signatures { margin-top: 60px; display: flex; justify-content: space-between; }
          .signature-box { width: 45%; }
          .signature-box h4 { color: #0E4C92; margin-bottom: 50px; border-bottom: 1px solid #0E4C92; padding-bottom: 5px; }
          .signature-line { border-bottom: 1px solid #333; margin-bottom: 5px; height: 40px; }
          .signature-label { font-size: 9pt; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Schlüsselübergabeprotokoll</h1>
          <p>GB Sylt GmbH - Ferienwohnungsverwaltung</p>
        </div>
        
        <div class="company-info">
          <h3>Reinigungsfirma</h3>
          <p><strong>${selectedCompany?.name}</strong></p>
          ${selectedCompany?.contact_person ? `<p>Ansprechpartner: ${selectedCompany.contact_person}</p>` : ''}
          ${selectedCompany?.phone ? `<p>Telefon: ${selectedCompany.phone}</p>` : ''}
          ${selectedCompany?.email ? `<p>E-Mail: ${selectedCompany.email}</p>` : ''}
        </div>
        
        <div class="meta">
          <span><strong>Datum:</strong> ${today}</span>
          <span><strong>Anzahl Objekte:</strong> ${linkedProperties.length}</span>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 50%">Objekt</th>
              <th style="width: 30%">Adresse</th>
              <th style="width: 20%; text-align: center;">Anzahl Schlüssel</th>
            </tr>
          </thead>
          <tbody>
            ${linkedProperties.map(prop => `
              <tr>
                <td>${prop.name}</td>
                <td>${prop.street || ''} ${prop.city || ''}</td>
                <td style="text-align: center; font-weight: bold;">${keyHandoverData[prop.id] || 1}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="2" style="text-align: right;">Gesamt:</td>
              <td style="text-align: center;">${getTotalKeys()}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="signatures">
          <div class="signature-box">
            <h4>GB Sylt GmbH</h4>
            <div class="signature-line"></div>
            <div class="signature-label">Name</div>
            <div class="signature-line"></div>
            <div class="signature-label">Unterschrift / Datum</div>
          </div>
          <div class="signature-box">
            <h4>Reinigungsfirma</h4>
            <div class="signature-line"></div>
            <div class="signature-label">Name</div>
            <div class="signature-line"></div>
            <div class="signature-label">Unterschrift / Datum</div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const printCleaningProtocol = () => {
    const printWindow = window.open('', '_blank');
    const today = new Date().toLocaleDateString('de-DE');
    
    // Combine central text + company-specific notes
    const combinedText = [centralProtocolText, formData.cleaning_protocol_notes]
      .filter(Boolean)
      .join('\n\n---\n\n');
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reinigungsprotokoll - ${selectedCompany?.name}</title>
        <style>
          @page { size: A4; margin: 20mm; }
          body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.6; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #0E4C92; padding-bottom: 15px; }
          .header h1 { color: #0E4C92; margin: 0 0 10px 0; font-size: 18pt; }
          .company-info { background: #f5f5f5; padding: 15px; margin-bottom: 25px; border-left: 4px solid #0E4C92; }
          .company-info h3 { margin: 0 0 10px 0; color: #0E4C92; }
          .meta { margin-bottom: 20px; }
          .content { margin-bottom: 40px; white-space: pre-wrap; }
          .content h3 { color: #0E4C92; margin-top: 20px; }
          .signatures { margin-top: 60px; display: flex; justify-content: space-between; }
          .signature-box { width: 45%; }
          .signature-box h4 { color: #0E4C92; margin-bottom: 50px; border-bottom: 1px solid #0E4C92; padding-bottom: 5px; }
          .signature-line { border-bottom: 1px solid #333; margin-bottom: 5px; height: 40px; }
          .signature-label { font-size: 9pt; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Reinigungsprotokoll / Qualitätsstandard</h1>
          <p>GB Sylt GmbH - Ferienwohnungsverwaltung</p>
        </div>
        
        <div class="company-info">
          <h3>Reinigungsfirma</h3>
          <p><strong>${selectedCompany?.name}</strong></p>
          ${selectedCompany?.contact_person ? `<p>Ansprechpartner: ${selectedCompany.contact_person}</p>` : ''}
          ${selectedCompany?.phone ? `<p>Telefon: ${selectedCompany.phone}</p>` : ''}
          ${selectedCompany?.email ? `<p>E-Mail: ${selectedCompany.email}</p>` : ''}
        </div>
        
        <div class="meta">
          <span><strong>Datum:</strong> ${today}</span>
        </div>
        
        <div class="content">
          ${combinedText || '<em>Kein Protokolltext hinterlegt.</em>'}
        </div>
        
        <div class="signatures">
          <div class="signature-box">
            <h4>GB Sylt GmbH</h4>
            <div class="signature-line"></div>
            <div class="signature-label">Name</div>
            <div class="signature-line"></div>
            <div class="signature-label">Unterschrift / Datum</div>
          </div>
          <div class="signature-box">
            <h4>Reinigungsfirma</h4>
            <div class="signature-line"></div>
            <div class="signature-label">Name</div>
            <div class="signature-line"></div>
            <div class="signature-label">Unterschrift / Datum</div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.contact_person && c.contact_person.toLowerCase().includes(searchTerm.toLowerCase())) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = companies.filter(c => c.status !== 'inaktiv').length;
  const inactiveCount = companies.filter(c => c.status === 'inaktiv').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cleaning-companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Reinigungsfirmen
          </h1>
          <p className="text-stone-500 mt-1">{activeCount} aktive Firmen</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => { setCentralTextEdit(centralProtocolText); setSettingsDialogOpen(true); }}
            variant="outline"
            className="rounded-full"
            data-testid="settings-protocol-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Standardtext
          </Button>
          <Button
            onClick={() => { resetForm(); setSelectedCompany(null); setDialogOpen(true); }}
            className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-full"
            data-testid="add-cleaning-company-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Firma
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Firmen suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-stone-200"
            data-testid="cleaning-company-search"
          />
        </div>
        {inactiveCount > 0 && (
          <Button
            variant={showInactive ? "default" : "outline"}
            onClick={() => setShowInactive(!showInactive)}
            className={`rounded-full ${showInactive ? 'bg-stone-600' : ''}`}
          >
            Inaktive ({inactiveCount})
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-semibold text-slate-900">Name</TableHead>
              <TableHead className="font-semibold text-slate-900">Ansprechpartner</TableHead>
              <TableHead className="font-semibold text-slate-900">E-Mail</TableHead>
              <TableHead className="font-semibold text-slate-900">Telefon</TableHead>
              <TableHead className="font-semibold text-slate-900">Status</TableHead>
              <TableHead className="font-semibold text-slate-900 text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-stone-500">
                  Keine Reinigungsfirmen gefunden
                </TableCell>
              </TableRow>
            ) : (
              filteredCompanies.map((company) => (
                <TableRow 
                  key={company.id} 
                  className={`hover:bg-stone-50 ${company.status === 'inaktiv' ? 'opacity-50' : ''}`}
                  data-testid={`cleaning-row-${company.id}`}
                >
                  <TableCell className="font-medium text-slate-900">{company.name}</TableCell>
                  <TableCell className="text-stone-600">
                    {company.contact_salutation && `${company.contact_salutation} `}{company.contact_person || '-'}
                  </TableCell>
                  <TableCell className="text-stone-600">{company.email}</TableCell>
                  <TableCell className="text-stone-600">{company.phone || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={company.status === 'inaktiv' ? 'secondary' : 'default'}
                      className={company.status === 'inaktiv' ? 'bg-stone-200' : 'bg-emerald-100 text-emerald-700'}>
                      {company.status === 'inaktiv' ? 'Inaktiv' : 'Aktiv'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {company.status === 'inaktiv' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReactivate(company)}
                          className="text-emerald-600 border-emerald-300 hover:bg-emerald-600 hover:text-white"
                          data-testid={`reactivate-${company.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reaktivieren
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(company)}
                            className="text-[#0E4C92] border-[#0E4C92] hover:bg-[#0E4C92] hover:text-white"
                            data-testid={`edit-cleaning-${company.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Bearbeiten
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedCompany(company); setDeleteDialogOpen(true); }}
                            className="text-red-600 border-red-300 hover:bg-red-600 hover:text-white"
                            data-testid={`delete-cleaning-${company.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Löschen
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit/Create Dialog with Tabs */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { resetForm(); setSelectedCompany(null); } setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {selectedCompany ? 'Reinigungsfirma bearbeiten' : 'Neue Reinigungsfirma'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCompany ? (
            <Tabs defaultValue="stammdaten" className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-4">
                <TabsTrigger value="stammdaten" className="flex items-center gap-1">
                  <Edit className="w-4 h-4" />
                  <span className="hidden sm:inline">Stammdaten</span>
                </TabsTrigger>
                <TabsTrigger value="objekte" className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Objekte</span>
                </TabsTrigger>
                <TabsTrigger value="schluessel" className="flex items-center gap-1">
                  <Key className="w-4 h-4" />
                  <span className="hidden sm:inline">Schlüssel</span>
                </TabsTrigger>
                <TabsTrigger value="protokoll" className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Protokoll</span>
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: Stammdaten */}
              <TabsContent value="stammdaten">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Firmenname *</Label>
                      <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="bg-stone-50" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_person">Ansprechpartner</Label>
                      <div className="flex gap-2">
                        <Select
                          value={formData.contact_salutation || 'none'}
                          onValueChange={(value) => setFormData({ ...formData, contact_salutation: value === 'none' ? '' : value })}
                        >
                          <SelectTrigger className="bg-stone-50 w-24">
                            <SelectValue placeholder="–" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">–</SelectItem>
                            <SelectItem value="Herr">Herr</SelectItem>
                            <SelectItem value="Frau">Frau</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input id="contact_person" value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} className="bg-stone-50 flex-1" placeholder="Name" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="bg-stone-50" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input id="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+49 ..." className="bg-stone-50" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notizen</Label>
                    <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="bg-stone-50 min-h-[80px]" />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full">Abbrechen</Button>
                    <Button type="submit" className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full" disabled={submitting}>
                      {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Speichern
                    </Button>
                  </div>
                </form>
              </TabsContent>

              {/* Tab 2: Verknüpfte Objekte */}
              <TabsContent value="objekte">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">Verknüpfte Objekte</h3>
                    <Badge variant="outline" className="text-[#0E4C92]">
                      {linkedProperties.length} Objekte
                    </Badge>
                  </div>
                  
                  {loadingLinked ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#0E4C92]" />
                    </div>
                  ) : linkedProperties.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">
                      <Building2 className="w-12 h-12 mx-auto mb-2 text-stone-300" />
                      <p>Keine Objekte zugeordnet</p>
                      <p className="text-sm mt-1">Objekte können in der Objektverwaltung zugewiesen werden.</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-stone-50">
                            <TableHead>Objekt</TableHead>
                            <TableHead>Adresse</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {linkedProperties.map(prop => (
                            <TableRow key={prop.id}>
                              <TableCell className="font-medium">{prop.name}</TableCell>
                              <TableCell className="text-stone-600">{prop.street} {prop.city}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Tab 3: Schlüsselübergabeprotokoll */}
              <TabsContent value="schluessel">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">Schlüsselübergabeprotokoll</h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[#0E4C92]">
                        Gesamt: {getTotalKeys()} Schlüssel
                      </Badge>
                      <Button onClick={printKeyProtocol} variant="outline" size="sm" className="rounded-full" disabled={linkedProperties.length === 0}>
                        <Printer className="w-4 h-4 mr-1" />
                        Drucken
                      </Button>
                    </div>
                  </div>
                  
                  {loadingLinked ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#0E4C92]" />
                    </div>
                  ) : linkedProperties.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">
                      <Key className="w-12 h-12 mx-auto mb-2 text-stone-300" />
                      <p>Keine Objekte für Schlüsselprotokoll</p>
                    </div>
                  ) : (
                    <>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-stone-50">
                              <TableHead>Objekt</TableHead>
                              <TableHead>Adresse</TableHead>
                              <TableHead className="w-32">Schlüssel</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {linkedProperties.map(prop => (
                              <TableRow key={prop.id}>
                                <TableCell className="font-medium">{prop.name}</TableCell>
                                <TableCell className="text-stone-600">{prop.street} {prop.city}</TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={keyHandoverData[prop.id] || 1}
                                    onChange={(e) => handleKeyCountChange(prop.id, e.target.value)}
                                    className="w-20 bg-stone-50 text-center"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={saveKeyHandover} className="bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full" disabled={savingProtocol}>
                          {savingProtocol && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                          Schlüsselprotokoll speichern
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </TabsContent>

              {/* Tab 4: Reinigungsprotokoll */}
              <TabsContent value="protokoll">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">Reinigungsprotokoll / Qualitätsstandard</h3>
                    <Button onClick={printCleaningProtocol} variant="outline" size="sm" className="rounded-full">
                      <Printer className="w-4 h-4 mr-1" />
                      Drucken
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-stone-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-semibold">Zentraler Standardtext</Label>
                        <Button variant="ghost" size="sm" onClick={() => { setCentralTextEdit(centralProtocolText); setSettingsDialogOpen(true); }}>
                          <Settings className="w-4 h-4 mr-1" />
                          Bearbeiten
                        </Button>
                      </div>
                      <div className="text-sm text-stone-600 whitespace-pre-wrap max-h-40 overflow-y-auto">
                        {centralProtocolText || <em className="text-stone-400">Kein Standardtext hinterlegt. Klicken Sie auf &quot;Bearbeiten&quot; um einen anzulegen.</em>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cleaning_protocol_notes" className="font-semibold">
                        Firmen-spezifische Ergänzung
                      </Label>
                      <p className="text-xs text-stone-500">Diese Ergänzung gilt nur für {selectedCompany?.name}</p>
                      <Textarea
                        id="cleaning_protocol_notes"
                        value={formData.cleaning_protocol_notes}
                        onChange={(e) => setFormData({...formData, cleaning_protocol_notes: e.target.value})}
                        placeholder="Zusätzliche Hinweise für diese Reinigungsfirma..."
                        className="bg-white min-h-[120px]"
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={handleSubmit} className="bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full" disabled={submitting}>
                        {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        Ergänzung speichern
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            /* Create Form (no tabs) */
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Firmenname *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="bg-stone-50" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Ansprechpartner</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.contact_salutation || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, contact_salutation: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className="bg-stone-50 w-24">
                        <SelectValue placeholder="–" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">–</SelectItem>
                        <SelectItem value="Herr">Herr</SelectItem>
                        <SelectItem value="Frau">Frau</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input id="contact_person" value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} className="bg-stone-50 flex-1" placeholder="Name" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="bg-stone-50" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input id="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+49 ..." className="bg-stone-50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notizen</Label>
                <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="bg-stone-50 min-h-[80px]" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full">Abbrechen</Button>
                <Button type="submit" className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full" disabled={submitting}>
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Erstellen
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Central Protocol Text Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Zentraler Reinigungsprotokoll-Standardtext</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-stone-600">
              Dieser Text wird für alle Reinigungsfirmen als Standardtext verwendet. 
              Jede Firma kann zusätzlich einen eigenen Ergänzungstext haben.
            </p>
            <Textarea
              value={centralTextEdit}
              onChange={(e) => setCentralTextEdit(e.target.value)}
              placeholder="Allgemeine Reinigungsstandards und Qualitätsanforderungen..."
              className="bg-stone-50 min-h-[300px]"
            />
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setSettingsDialogOpen(false)} className="flex-1 rounded-full">
                Abbrechen
              </Button>
              <Button onClick={saveCentralProtocolText} className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full" disabled={savingProtocol}>
                {savingProtocol && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reinigungsfirma deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedCompany?.name} wird deaktiviert und ist nicht mehr sichtbar. Die Daten bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-full" data-testid="confirm-delete-cleaning">
              Deaktivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CleaningCompaniesPage;
